import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { mockReviews } from '@/mocks/products';

interface Review {
  id: string;
  avatar: string;
  rating: number;
  date: string;
}

const isSupabaseConnected = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
  return Boolean(url && key && url.startsWith('https://'));
};

export default function ReviewsSection() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState(mockReviews);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchReviews = async () => {
      if (!isSupabaseConnected()) {
        if (!cancelled) {
          setReviews(mockReviews);
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await supabase
          .from('reviews')
          .select('*')
          .eq('is_visible', true)
          .order('created_at', { ascending: false })
          .limit(4);

        if (!cancelled && data && data.length > 0) {
          setReviews(data as Review[]);
        } else if (!cancelled) {
          setReviews(mockReviews);
        }
      } catch {
        if (!cancelled) setReviews(mockReviews);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReviews();
    return () => { cancelled = true; };
  }, []);

  const reviewKeys = [
    { id: '1', nameKey: 'review1_name', contentKey: 'review1_content', productKey: 'review1_product' },
    { id: '2', nameKey: 'review2_name', contentKey: 'review2_content', productKey: 'review2_product' },
    { id: '3', nameKey: 'review3_name', contentKey: 'review3_content', productKey: 'review3_product' },
    { id: '4', nameKey: 'review4_name', contentKey: 'review4_content', productKey: 'review4_product' },
  ];

  if (loading) {
    return (
      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#f0ebe4] animate-pulse">
                <div className="h-4 bg-[#f0ebe4] rounded w-20 mb-4" />
                <div className="h-16 bg-[#f0ebe4] rounded mb-5" />
                <div className="h-6 bg-[#f0ebe4] rounded w-24 mb-5" />
                <div className="flex items-center gap-3 pt-4 border-t border-[#f0ebe4]">
                  <div className="w-9 h-9 bg-[#f0ebe4] rounded-full" />
                  <div className="h-4 bg-[#f0ebe4] rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] mb-3">
            {t('reviews_title')}
          </h2>
          <p className="text-[#888] text-sm">{t('reviews_subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((review, idx) => (
            <div
              key={review.id}
              className="bg-white rounded-2xl p-6 border border-[#f0ebe4]"
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#f5a623] text-sm"></i>
                ))}
              </div>

              <p className="text-sm text-[#555] leading-relaxed mb-5">
                {t(reviewKeys[idx]?.contentKey || '')}
              </p>

              <div className="inline-block px-2.5 py-1 rounded-full bg-[#C8714A]/10 text-[#C8714A] text-xs mb-5">
                {t(reviewKeys[idx]?.productKey || '')}
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-[#f0ebe4]">
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={review.avatar}
                    alt={t(reviewKeys[idx]?.nameKey || '')}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1a1a1a]">{t(reviewKeys[idx]?.nameKey || '')}</div>
                  <div className="text-xs text-[#bbb]">{review.date}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}